// #1580 — per-departure-city rates section of the Russia carriers tab.
//
// Renders one section per `RussiaRequestStartCity`. Each section lists the
// START_CITY-scope bids (one per carrier) for that city, an "Add rate" CTA
// per carrier that has not yet quoted the city, a winner pick per bid, and a
// clear "no rate yet" state for cities with no bid at all.
//
// @aiPurpose presentational + arbitration of the per-city bid modal; the
//   parent CarriersTab owns the global modal/confirm state, so this only
//   raises callbacks. LOCAL component (no China-component import) per the
//   #1578 local-component rule.
// @aiSecurity start-city is reference data (arrivalCityId), NOT new PII; the
//   only carrier identity shown is the PII-gated `nameSnapshot` already on
//   the wire (falls back to displayCode when stripped).
// @aiPii resolves arrivalCityId → city name via the shared active
//   arrival-cities catalog, the same source StartCityChips uses.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RussiaRequest, RussiaRequestCarrier, RussiaRequestCarrierBid } from '@magimp/shared';

import { useActiveArrivalCities } from '../../../../catalog/index.js';
import { Button } from '../../../../ui/index.js';

export interface PerCityBidTarget {
  carrierId: string;
  carrierName: string;
  arrivalCityId: string;
  bid?: RussiaRequestCarrierBid;
}

export interface RussiaPerCityRatesProps {
  request: RussiaRequest;
  carriers: ReadonlyArray<RussiaRequestCarrier>;
  canEdit: boolean;
  canPickWinner: boolean;
  /** Open the per-city add/edit bid modal for a (carrier, city) pair. */
  onAddOrEditRate: (target: PerCityBidTarget) => void;
  onPickWinner: (bid: RussiaRequestCarrierBid) => void;
}

export function RussiaPerCityRates({
  request,
  carriers,
  canEdit,
  canPickWinner,
  onAddOrEditRate,
  onPickWinner,
}: RussiaPerCityRatesProps) {
  const { t } = useTranslation('russiaRequests');
  const startCities = request.startCities;
  const citiesQuery = useActiveArrivalCities({ enabled: startCities.length > 0 });
  const cityNameById = useMemo(
    () => new Map((citiesQuery.data ?? []).map((c) => [c.id, c.name])),
    [citiesQuery.data],
  );

  if (startCities.length === 0) return null;

  const ordered = [...startCities].sort((a, b) => a.sortOrder - b.sortOrder);
  // Active (non-rejected) carriers may carry / receive a per-city rate.
  const activeCarriers = carriers.filter((c) => c.state !== 'REJECTED');

  return (
    <section
      data-testid="russia-request-drawer-per-city-rates"
      style={{ display: 'grid', gap: 14 }}
    >
      <span
        style={{
          fontSize: 10,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--fg-3)',
        }}
      >
        {t('drawer.carriers.perCity.heading')}
      </span>
      {ordered.map((sc) => {
        const cityName = cityNameById.get(sc.arrivalCityId) ?? sc.arrivalCityId.slice(0, 6);
        const rates = activeCarriers
          .map((carrier) => ({
            carrier,
            bid: carrier.bids.find(
              (b) => b.scope === 'START_CITY' && b.scopeRefId === sc.arrivalCityId,
            ),
          }))
          .filter((r): r is { carrier: RussiaRequestCarrier; bid: RussiaRequestCarrierBid } =>
            Boolean(r.bid),
          );
        const carriersWithoutRate = activeCarriers.filter(
          (carrier) =>
            !carrier.bids.some(
              (b) => b.scope === 'START_CITY' && b.scopeRefId === sc.arrivalCityId,
            ),
        );
        return (
          <div
            key={sc.arrivalCityId}
            data-testid={`russia-request-drawer-per-city-${sc.arrivalCityId}`}
            style={{
              border: '1px solid var(--border-1)',
              borderRadius: 'var(--r-2)',
              padding: 12,
              display: 'grid',
              gap: 8,
              background: 'var(--bg-1)',
            }}
          >
            <strong style={{ fontSize: 13 }}>{cityName}</strong>
            {rates.length === 0 ? (
              <span
                data-testid={`russia-request-drawer-per-city-empty-${sc.arrivalCityId}`}
                style={{ color: 'var(--fg-3)', fontSize: 12 }}
              >
                {t('drawer.carriers.perCity.noRate')}
              </span>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                {rates.map(({ carrier, bid }) => (
                  <PerCityRateRow
                    key={bid.id}
                    carrier={carrier}
                    bid={bid}
                    isWinner={bid.isWinner}
                    canEdit={canEdit}
                    canPickWinner={canPickWinner && carrier.state === 'ANSWERED'}
                    onEdit={() =>
                      onAddOrEditRate({
                        carrierId: carrier.id,
                        carrierName: carrier.nameSnapshot ?? carrier.displayCode,
                        arrivalCityId: sc.arrivalCityId,
                        bid,
                      })
                    }
                    onPickWinner={() => onPickWinner(bid)}
                  />
                ))}
              </ul>
            )}
            {canEdit &&
              carriersWithoutRate.map((carrier) => (
                <div key={carrier.id}>
                  <Button
                    kind="secondary"
                    size="sm"
                    icon="plus"
                    data-testid={`russia-request-drawer-per-city-add-${sc.arrivalCityId}-${carrier.id}`}
                    onClick={() =>
                      onAddOrEditRate({
                        carrierId: carrier.id,
                        carrierName: carrier.nameSnapshot ?? carrier.displayCode,
                        arrivalCityId: sc.arrivalCityId,
                      })
                    }
                  >
                    {t('drawer.carriers.perCity.addRate', {
                      carrier: carrier.nameSnapshot ?? carrier.displayCode,
                    })}
                  </Button>
                </div>
              ))}
          </div>
        );
      })}
    </section>
  );
}

function formatPrice(price: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'decimal',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return String(price);
  }
}

function PerCityRateRow({
  carrier,
  bid,
  isWinner,
  canEdit,
  canPickWinner,
  onEdit,
  onPickWinner,
}: {
  carrier: RussiaRequestCarrier;
  bid: RussiaRequestCarrierBid;
  isWinner: boolean;
  canEdit: boolean;
  canPickWinner: boolean;
  onEdit: () => void;
  onPickWinner: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  return (
    <li
      data-testid={`russia-request-drawer-per-city-rate-${bid.id}`}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        padding: '6px 8px',
        borderRadius: 'var(--r-1)',
        background: isWinner ? 'var(--status-success-bg)' : 'var(--bg-2)',
        fontSize: 13,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ color: 'var(--fg-2)', minWidth: 0, overflowWrap: 'anywhere' }}>
        {carrier.nameSnapshot ?? carrier.displayCode}
      </span>
      <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(bid.price)}</strong>
      {isWinner && (
        <span
          data-testid={`russia-request-drawer-per-city-winner-${bid.id}`}
          style={{
            fontSize: 10,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'var(--status-success-fg)',
            padding: '1px 6px',
            borderRadius: 'var(--r-1)',
            border: '1px solid var(--status-success-fg)',
          }}
        >
          {t('drawer.carriers.winner.badge')}
        </span>
      )}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        {canPickWinner && !isWinner && (
          <Button
            kind="secondary"
            size="sm"
            icon="check-check"
            data-testid={`russia-request-drawer-per-city-pick-${bid.id}`}
            onClick={onPickWinner}
          >
            {t('drawer.carriers.winner.pick')}
          </Button>
        )}
        {canEdit && (
          <Button
            kind="ghost"
            size="sm"
            data-testid={`russia-request-drawer-per-city-edit-${bid.id}`}
            onClick={onEdit}
          >
            {t('drawer.carriers.perCity.editRate')}
          </Button>
        )}
      </span>
    </li>
  );
}
